import { isRedirect, navigate } from '@reach/router';
import * as Sentry from '@sentry/react';
import { Component } from 'react';

const wrapper = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  textAlign: 'center',
  fontFamily: 'Poppins, sans-serif',
  color: '#002334'
};

const button = {
  marginTop: '24px',
  minWidth: '220px',
  minHeight: '50px',
  borderRadius: '9999px',
  border: '2px solid #3722d3',
  background: '#3722d3',
  color: '#ffffff',
  fontSize: '16px',
  fontWeight: 600,
  cursor: 'pointer'
};

const detail = {
  marginTop: '24px',
  maxWidth: '600px',
  fontSize: '12px',
  color: '#6b7280',
  wordBreak: 'break-word'
};

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    // Reach Router signals redirects by throwing them, so they must reach the
    // router rather than be rendered as a failure.
    return isRedirect(error) ? null : { error };
  }

  componentDidCatch(error, info) {
    if (isRedirect(error)) {
      navigate(error.uri, { replace: true });
      return;
    }

    // A new deploy rotates hashed chunk filenames, so an already-loaded
    // index.html can request chunks that no longer exist.
    if (error?.name === 'ChunkLoadError') {
      window.location.reload();
      return;
    }

    console.error('Unhandled render error:', error, info?.componentStack);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info?.componentStack } }
    });
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;

    if (!error) return children;

    if (error.name === 'ChunkLoadError') {
      return <div style={wrapper}>Updating to the latest version…</div>;
    }

    return (
      <div style={wrapper}>
        <h1>Something went wrong</h1>
        <p>
          This page failed to load. Reloading usually fixes it — if it keeps
          happening, let the Cove team know.
        </p>
        <button
          type="button"
          style={button}
          onClick={() => window.location.reload()}
        >
          Reload page
        </button>
        <p style={detail}>{error.message}</p>
      </div>
    );
  }
}

export default ErrorBoundary;
